import {
  OnboardingAction,
  OnboardingFileCategory,
  OnboardingView,
} from '@chiroup/core/types/Onboarding.type';
import {
  Check,
  ChevronDown,
  Download,
  FileCheck2,
  FileSpreadsheet,
  FolderLock,
  History,
  UploadCloud,
} from 'lucide-react';
import { ChangeEvent, useState } from 'react';
import onboardingService from '../../services/onboarding.service';
import OnboardingConversation from './OnboardingConversation';
import {
  calendarLabel,
  errorMessage,
  instantLabel,
  secondaryClass,
  Section,
  Status,
} from './OnboardingUi';

type Props = {
  clinicId: number;
  view: OnboardingView;
  canReview: boolean;
  busy: boolean;
  act: (action: OnboardingAction) => Promise<unknown>;
  upload: (input: {
    file: File;
    category: OnboardingFileCategory;
    fileId?: string;
  }) => Promise<unknown>;
};

const OnboardingFiles = ({ clinicId, view, canReview, busy, act, upload }: Props) => {
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const editable = canReview && view.availability.active && view.config.migrationApplicable;
  const perform = async (action: OnboardingAction) => {
    setError('');
    try {
      await act(action);
      setDeletingId(null);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };
  const addFiles = async (
    event: ChangeEvent<HTMLInputElement>,
    category: OnboardingFileCategory,
    fileId?: string
  ) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    setError('');
    for (const file of files) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (
        !extension ||
        !(category === 'consent' ? ['pdf'] : ['csv', 'xls', 'xlsx']).includes(extension)
      ) {
        setError('Choose CSV, XLS, or XLSX migration files, or PDF consent forms.');
        return;
      }
      if (file.size > 25 * 1024 * 1024 || file.size === 0) {
        setError('Choose a non-empty file no larger than 25 MB.');
        return;
      }
    }
    try {
      for (const file of files) await upload({ file, category, fileId });
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };
  const download = async (fileId: string, versionId?: string) => {
    setError('');
    try {
      const result = await onboardingService.download(clinicId, fileId, versionId);
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };
  const categories: OnboardingFileCategory[] = ['test', 'final', 'consent'];
  return (
    <Section
      title="Data Migration"
      eyebrow="Secure migration"
      tone="violet"
      icon={<FolderLock className="h-5 w-5" />}
      description="Private clinic submissions, independent file reviews, and complete version history. Readiness is confirmed by an authorized internal reviewer."
      action={
        <Status
          value={view.config.migrationApplicable ? view.config.readiness : 'not_applicable'}
        />
      }
    >
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            ['Migration date', view.config.migrationDate],
            ['Test files due', view.config.testFileDueDate],
            ['Final files due', view.config.finalFileDueDate],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl bg-muted/40 p-4">
              <p className="text-base text-muted-foreground sm:text-sm">{label}</p>
              <p className="mt-1 text-lg font-semibold tabular-nums">
                {view.config.migrationApplicable ? calendarLabel(value) : 'Not Applicable'}
              </p>
            </div>
          ))}
        </div>
        <a
          href={view.content.migrationGuideUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={secondaryClass}
        >
          Open Migration Guide ↗
        </a>
        {!view.config.migrationApplicable && (
          <p className="text-base text-muted-foreground sm:text-sm">
            Migration is Not Applicable. Historical submissions remain available internally below.
          </p>
        )}
        {error && (
          <p role="alert" className="text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
        {categories.map(category => {
          const files = view.files.filter(file => file.category === category);
          return (
            <div key={category} className="space-y-4 rounded-xl border border-border p-4 sm:p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-700 dark:bg-violet-950/35 dark:text-violet-300">
                    {category === 'consent' ? (
                      <FileCheck2 className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <FileSpreadsheet className="h-5 w-5" aria-hidden="true" />
                    )}
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-semibold">
                      {category === 'test'
                        ? 'Test Files'
                        : category === 'final'
                          ? 'Final Files'
                          : 'Signed Consent Forms'}
                    </h3>
                    {view.migrationOverdue[category] && (
                      <div className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-base font-medium text-amber-900 sm:text-sm">
                        Overdue · accepted submission needed
                      </div>
                    )}
                    <p className="text-base text-muted-foreground sm:text-sm">
                      {category === 'consent'
                        ? view.config.consentRequired
                          ? 'Required · an accepted PDF is needed before Ready'
                          : 'Not Applicable · consent is not required'
                        : 'CSV, XLS, XLSX · up to 25 MB per file'}
                    </p>
                  </div>
                </div>
                {editable && (category !== 'consent' || view.config.consentRequired) && (
                  <label className={`${secondaryClass} cursor-pointer`}>
                    <UploadCloud aria-hidden="true" className="h-4 w-4" />
                    Add files
                    <input
                      type="file"
                      name={`upload-${category}`}
                      aria-label={`Upload ${category} files`}
                      className="sr-only"
                      accept={category === 'consent' ? '.pdf' : '.csv,.xls,.xlsx'}
                      multiple
                      disabled={busy}
                      onChange={event => addFiles(event, category)}
                    />
                  </label>
                )}
              </div>
              {!files.length && (
                <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-violet-500/20 bg-violet-50/30 px-4 py-7 text-center dark:bg-violet-950/15">
                  <UploadCloud
                    className="h-7 w-7 text-violet-600 dark:text-violet-300"
                    aria-hidden="true"
                  />
                  <p className="font-medium">No submissions yet.</p>
                  <p className="max-w-[45ch] text-base text-muted-foreground sm:text-sm">
                    Files will appear here as the clinic submits them for review.
                  </p>
                </div>
              )}
              {files.map(file => {
                const current = file.versions[file.versions.length - 1];
                if (!current) return null;
                return (
                  <article
                    key={file.id}
                    className="space-y-4 rounded-xl border border-border bg-muted/20 p-4"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <FileSpreadsheet
                          className="h-6 w-6 shrink-0 text-violet-700 dark:text-violet-300"
                          aria-hidden="true"
                        />
                        <div className="space-y-1">
                          <h4 className="break-all font-semibold">{current.name}</h4>
                          <p className="text-base text-muted-foreground sm:text-sm">
                            {instantLabel(current.uploadedAt)} · {current.uploadedBy.name} ·{' '}
                            {(current.size / 1024).toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}{' '}
                            KB
                          </p>
                          {file.deletedAt && (
                            <p className="text-base text-muted-foreground sm:text-sm">
                              Removed from clinic view · {instantLabel(file.deletedAt)}
                            </p>
                          )}
                        </div>
                      </div>
                      <Status value={current.reviewStatus} />
                    </div>
                    {current.reviewedBy && (
                      <p className="text-base text-muted-foreground sm:text-sm">
                        Reviewed by {current.reviewedBy.name} · {instantLabel(current.reviewedAt)}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className={secondaryClass}
                        onClick={() => download(file.id, current.id)}
                      >
                        <Download className="h-4 w-4" aria-hidden="true" />
                        Download current version
                      </button>
                      {editable && !file.deletedAt && (
                        <>
                          <label className={`${secondaryClass} cursor-pointer`}>
                            Replace file
                            <input
                              type="file"
                              name={`replace-${file.id}`}
                              aria-label={`Replace ${current.name}`}
                              className="sr-only"
                              accept={category === 'consent' ? '.pdf' : '.csv,.xls,.xlsx'}
                              disabled={busy}
                              onChange={event => addFiles(event, category, file.id)}
                            />
                          </label>
                          <button
                            type="button"
                            className={secondaryClass}
                            disabled={busy}
                            onClick={() => setDeletingId(file.id)}
                          >
                            Remove file
                          </button>
                          <button
                            type="button"
                            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-emerald-600/20 bg-emerald-50 px-3 py-2 text-base font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200 dark:hover:bg-emerald-900/40 sm:min-h-10 sm:text-sm"
                            disabled={busy || current.reviewStatus === 'accepted'}
                            onClick={() =>
                              perform({
                                type: 'reviewFile',
                                fileId: file.id,
                                versionId: current.id,
                                status: 'accepted',
                              })
                            }
                          >
                            <Check className="h-4 w-4" aria-hidden="true" />
                            Accept
                          </button>
                          <button
                            type="button"
                            className={secondaryClass}
                            disabled={busy || current.reviewStatus === 'needs_correction'}
                            onClick={() =>
                              perform({
                                type: 'reviewFile',
                                fileId: file.id,
                                versionId: current.id,
                                status: 'needs_correction',
                              })
                            }
                          >
                            Needs Correction
                          </button>
                        </>
                      )}
                    </div>
                    {deletingId === file.id && (
                      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-500/20 bg-amber-50 p-4 text-amber-950 dark:bg-amber-950/25 dark:text-amber-200">
                        <p className="flex-1">
                          Remove this submission from the clinic’s view? Its versions and
                          conversation will remain in internal history.
                        </p>
                        <button
                          type="button"
                          className={secondaryClass}
                          disabled={busy}
                          onClick={() => perform({ type: 'deleteFile', fileId: file.id })}
                        >
                          Remove submission
                        </button>
                        <button
                          type="button"
                          className={secondaryClass}
                          onClick={() => setDeletingId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    <details className="group/history">
                      <summary className="flex cursor-pointer list-none items-center gap-2 rounded-lg py-2 text-base font-medium hover:text-violet-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-700 dark:hover:text-violet-300 dark:focus-visible:outline-violet-400 sm:text-sm">
                        <History className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                        <p className="flex-1">Version history · {file.versions.length}</p>
                        <ChevronDown
                          className="h-4 w-4 text-muted-foreground motion-safe:transition-transform group-open/history:rotate-180"
                          aria-hidden="true"
                        />
                      </summary>
                      <ol role="list" className="mt-3 space-y-3">
                        {[...file.versions].reverse().map((version, index) => (
                          <li
                            key={version.id}
                            className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border bg-background p-3"
                          >
                            <div className="space-y-1">
                              <p className="break-all font-medium">
                                {index === 0 ? 'Current' : 'Previous'} · {version.name}
                              </p>
                              <p className="text-base text-muted-foreground sm:text-sm">
                                {instantLabel(version.uploadedAt)} · {version.uploadedBy.name}
                              </p>
                              {version.reviewedBy && (
                                <p className="text-base text-muted-foreground sm:text-sm">
                                  Reviewed by {version.reviewedBy.name} ·{' '}
                                  {instantLabel(version.reviewedAt)}
                                </p>
                              )}
                              <Status value={version.reviewStatus} />
                            </div>
                            <button
                              type="button"
                              className={secondaryClass}
                              onClick={() => download(file.id, version.id)}
                            >
                              Download
                            </button>
                          </li>
                        ))}
                      </ol>
                    </details>
                    <OnboardingConversation
                      conversation={file.conversation}
                      target="file"
                      targetId={file.id}
                      editable={view.availability.active && !file.deletedAt}
                      busy={busy}
                      act={act}
                    />
                  </article>
                );
              })}
            </div>
          );
        })}
      </div>
    </Section>
  );
};

export default OnboardingFiles;
